import {
  trackCompositionTranslationCompleted,
  trackCompositionTranslationFailed,
  trackCompositionTranslationStarted,
  trackTranslationSrtUploadCompleted,
  trackTranslationSrtUploadFailed,
  trackTranslationSrtUploadStarted,
  trackTranslationToggled,
} from '@src/analytics/EventTracker';
import { withTranslationTracking } from '@src/analytics/withTranslationTracking';
import {
  DialogCard,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  PrimaryButton,
  SecondaryButton,
} from '@src/components/common/dialogStyles';
import Modal from '@src/components/common/Modal';
import { useAnchoredDropdown } from '@src/components/common/useAnchoredDropdown';
import { estimateTranslateMs } from '@src/config/processingEstimates';
import { TranslationApiError } from '@src/repositories/TranslationRepository';
import {
  useStore,
  useTranscription,
  useTranslationStore,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import { isAbortError } from '@src/utility/error';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import { extractTranslatableElements } from '@src/videoTranscriptionProcessor/translation/extractTranslatableElements';
import { getTranslatableSourceElements } from '@src/videoTranscriptionProcessor/translation/getTranslatableSourceElements';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { countDistinctTmEntries } from './changedPhraseKit';
import {
  resolvePreviousSource,
  TRANSLATION_SOURCES,
} from './translationProviderDisplay';
import { useTranslationRunProgress } from './useTranslationRunProgress';

import type { TranslationSourceId } from './translationProviderDisplay';
import type { Language } from '@src/types.ts/language';
import type { TranslationMemoryReport } from '@src/types.ts/translation';
import type { CompositionTranslationOutcome } from '@src/videoTranscriptionProcessor/translation/compositionTypes';

export interface TranslationGenerateModalProps {
  /** Called when the user cancels or after a successful generation closes. */
  onClose: () => void;

  /** Fires with the target language on successful completion. */
  onSuccess?: (targetLanguage: string) => void;

  /** Optional prefilled target (bypasses the dropdown pick). */
  initialTargetLanguage?: string;

  /**
   * Showcase-enabled languages — same `Language[]` Brand Kit reads. Source
   * for the target-language dropdown and for resolving the source-language
   * caption.
   */
  supportedLanguages: Language[];

  /**
   * Optional async hook awaited immediately before generate/regenerate AND
   * before an SRT upload runs — after the user has committed but before any
   * server call. The Speakers & Transcripts host passes
   * `adapter.reindexAndPersistForTranslation` here so the canonical transcript
   * on S3 (what the server translates, since `elements` is intentionally not
   * shipped) is re-densified and current: no `-1` content indices, matching
   * what the editor renders. Omitted by hosts that don't own an editor.
   */
  onBeforeGenerate?: () => Promise<void>;

  /**
   * PLAT-11574 — optional action for the "translation is ready" toast, built
   * for the language that was just translated. Hosts that own the Translation
   * Review workspace pass a "Review Translations" action so checking the
   * result is one click from the toast. Omitted ⇒ plain toast. Only fires on
   * the LLM-generate success path; SRT uploads end in their own coverage step.
   */
  successAction?: (
    targetLanguage: string,
  ) => { label: string; onClick: () => void } | undefined;
  /** PLAT-11877 — a second, non-dismissing toast action beside `successAction`
   *  ("Copy Review Link"). Same contract: built for the translated language. */
  successSecondaryAction?: (
    targetLanguage: string,
  ) => { label: string; onClick: () => void } | undefined;
}

/**
 * The popover's own phases — the SRT path's. The LLM generate has no phase
 * here any more: PLAT-11690 moved that run into `TranslationStore.activeRun`,
 * so pressing Generate closes the popover and the on-page chip carries it; its
 * outcome (ready, failed, compositions failed) arrives by toast.
 */
type Phase =
  | { kind: 'idle' }
  | { kind: 'uploading'; filename: string }
  | {
      kind: 'uploaded';
      coveragePercent: number;
      uncoveredCount: number;
    }
  | { kind: 'error'; message: string };

/**
 * Client-side SRT size ceiling. Human-authored SRTs for long-form video
 * top out in the low hundreds of KB; anything past 5 MiB is almost
 * certainly a renamed video file or a pathological export. Reject before
 * the `await file.text()` read so we don't OOM the renderer or hit the
 * API gateway's 413 with no friendly message.
 */
const MAX_SRT_BYTES = 5 * 1024 * 1024;

export const TranslationGenerateModal: React.FC<TranslationGenerateModalProps> =
  observer(function TranslationGenerateModal({
    onClose,
    onSuccess,
    onBeforeGenerate,
    successAction,
    successSecondaryAction,
    initialTargetLanguage,
    supportedLanguages,
  }) {
    const translationStore = useTranslationStore();
    const transcription = useTranscription();
    const rootStore = useStore();
    const videoCreator = useVideoCreatorStore();

    // Lookup is case-insensitive — transcription.language may come through as
    // lower-cased (e.g. "en-us") while showcase Language records use canonical
    // BCP 47 casing ("en-US"). Key on lowercase on both sides.
    const byCode = useMemo(() => {
      const m = new Map<string, Language>();
      for (const l of supportedLanguages) m.set(l.value.toLowerCase(), l);
      return m;
    }, [supportedLanguages]);

    const sourceCode = transcription.language;
    const sourceLang = byCode.get(sourceCode.toLowerCase());
    // Fall back to Intl.DisplayNames when the source isn't in the showcase's
    // target-language list — showcases often enable target languages without
    // re-listing the source, so `sourceLang` is frequently undefined.
    const sourceCaption =
      sourceLang?.caption ?? formatLanguageCaption(sourceCode);

    const targetOptions = useMemo(() => {
      const translated = new Set(
        Array.from(translationStore.translations.keys()).map(k =>
          k.toLowerCase(),
        ),
      );
      const sourceLower = sourceCode.toLowerCase();
      return supportedLanguages
        .filter(
          l =>
            l.value.toLowerCase() !== sourceLower &&
            !translated.has(l.value.toLowerCase()),
        )
        .map(l => ({ value: l.value, caption: l.caption }))
        .sort((a, b) =>
          a.caption.localeCompare(b.caption, undefined, {
            sensitivity: 'base',
          }),
        );
    }, [supportedLanguages, translationStore.translations, sourceCode]);

    // Replace mode: opened with a preselect that already has a translation.
    // Computed once at mount because the target is locked when isReplace is
    // true (rendered as a read-only field, not the combobox), so the value
    // can't drift. If we ever unlock the target in replace mode, this needs
    // to switch to a per-render derivation. PLAT-9781.
    const [isReplace] = useState<boolean>(
      () =>
        initialTargetLanguage !== undefined &&
        translationStore.translations.has(initialTargetLanguage),
    );

    const [targetCode, setTargetCode] = useState<string | null>(
      initialTargetLanguage ?? null,
    );
    const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
    // Default the source picker to the provider that produced the existing
    // translation when in replace mode, so a no-op Replace runs the same
    // method that's currently installed. Falls back to the global default
    // for create mode and for legacy translations whose handler.id we
    // don't have in the map.
    const [sourceId, setSourceId] = useState<TranslationSourceId>(() => {
      if (initialTargetLanguage) {
        const previous = resolvePreviousSource(
          translationStore.translations.get(initialTargetLanguage),
        );
        if (previous) return previous.sourceId;
      }
      return 'openai-llm';
    });
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const selectedSource =
      TRANSLATION_SOURCES.find(s => s.id === sourceId) ??
      TRANSLATION_SOURCES[0];
    // The SRT upload's cancel handle. The LLM run's lives in the store.
    const abortRef = useRef<AbortController | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // PLAT-11690 — a translation already running on this story, if any. One at
    // a time: Generate is inert while it runs, and reopening the popover on the
    // language being translated shows the run rather than a fresh form.
    const activeRun = translationStore.activeRun;
    const runningHere =
      activeRun !== null && activeRun.storyId === rootStore.storyId
        ? activeRun
        : null;
    const runningThisTarget =
      runningHere !== null &&
      targetCode !== null &&
      runningHere.targetLanguage.toLowerCase() === targetCode.toLowerCase()
        ? runningHere
        : null;
    const runProgress = useTranslationRunProgress(
      runningThisTarget !== null,
      runningThisTarget ?? { etaMs: 0, startedAt: 0 },
    );

    // In replace mode the target is locked to the preselect, so it bypasses
    // the create-mode `targetOptions` filter (which excludes already-
    // translated languages — that's the create-mode invariant).
    const targetReady = isReplace
      ? targetCode !== null
      : targetCode !== null && targetOptions.some(o => o.value === targetCode);

    // Re-resolved against current target so a later target switch (theory
    // only — replace mode locks target today) keeps the helper / notice
    // honest. Reactive: reads the translations Map under the observer.
    const previousSource =
      isReplace && targetCode !== null
        ? resolvePreviousSource(translationStore.translations.get(targetCode))
        : null;
    const showMethodChangeNotice =
      previousSource !== null && previousSource.sourceId !== sourceId;
    const isPickingFromUI = phase.kind !== 'uploading';
    // The primary action ("Generate") is always the same control. Whether
    // it runs the LLM translator or the SRT upload depends on the source
    // picker; SRT additionally requires a staged file. One translation at a
    // time (PLAT-11690): a second Generate would silently abandon the first.
    const canGenerate =
      isPickingFromUI &&
      runningHere === null &&
      targetReady &&
      (sourceId === 'openai-llm' || selectedFile !== null);

    const targetCaption =
      targetOptions.find(o => o.value === targetCode)?.caption ??
      byCode.get((targetCode ?? '').toLowerCase())?.caption ??
      (targetCode !== null ? formatLanguageCaption(targetCode) : null);

    const handleGenerate = async () => {
      if (!canGenerate || targetCode === null) return;
      const storyId = rootStore.storyId ?? undefined;
      const languageCaption = targetCaption ?? targetCode;
      // The selected Translation Source dictates the handler. We pass it
      // explicitly so the user's pick wins over the showcase cascade
      // default (otherwise picking "OpenAI LLM Translation" wouldn't
      // actually do anything when the showcase default is something
      // else). The minimal `TranslationHandler` shape the repository
      // accepts is `{ id, provider, version }`; the repository unpacks
      // `id` into `handlerInternalName` on the wire.
      // TODO(PLAT-9780): replace this hardcoded shape with a read off
      // `TranslationHandlerStore` so `provider` reflects the real handler
      // record (Anthropic, Google, etc.) instead of always 'openai'.
      const handler = selectedSource.handlerInternalName
        ? {
            id: selectedSource.handlerInternalName,
            provider: 'openai' as const,
            version: 'v1',
          }
        : undefined;
      // Build the composition targets payload at click-time from the editor's
      // canonical serialization (`toStoredJSON`) rather than
      // `VideoSourceStore.source`. The source store holds the windowed preview
      // serialization, which drops elements outside the current playhead window
      // and reshuffles track order — translation must see every authored
      // element. `rootStore.editorStore` resolves to the story editor in Creator
      // Studio and, via `createClipRootProxy`, to the clip editor in clip
      // preview. `extraElementData` stays on `currentVideo` — that bucket IS
      // mirrored into the variant by the adapter's reaction at construction.
      const currentVideo = videoCreator.currentVideo;
      const extract = extractTranslatableElements({
        videoSourceElements: getTranslatableSourceElements(
          rootStore.editorStore.editor,
        ),
        extraElementData:
          (currentVideo?.extraElementData as
            | Record<string, unknown>
            | undefined) ?? null,
        targetLanguage: targetCode,
      });
      const compositionTargets = extract.compositionTargets;
      const willTranslateCompositions = compositionTargets.length > 0;
      // Closure-captured outcome so we can react to it after the tracking
      // wrapper resolves — withTranslationTracking deliberately keeps its
      // `run: () => Promise<void>` shape for callers that ignore returns.
      let compositionsOutcome: CompositionTranslationOutcome | undefined;
      // Same reason (PLAT-10924): the run's TM QA report only exists on the
      // response, and `readResult` can't await the store call itself.
      let tmReport: TranslationMemoryReport | undefined;
      if (willTranslateCompositions) {
        trackCompositionTranslationStarted({
          story_id: storyId,
          language: targetCode,
          target_count: compositionTargets.length,
          unique_phrases: extract.uniqueSourceTextCount,
          preset_skipped: extract.presetSkipped,
          from_bulk: true,
        });
      }
      const tComp0 = performance.now();
      // PLAT-11690 — start the run, then get out of the way at once. The page
      // carries it from here (`TranslationRunChip`: language, estimated percent,
      // Cancel), so holding the screen to replay the same progress would make
      // the creator dismiss a popover to reach the work they were doing.
      // `startRun` registers the run synchronously before its first await, so
      // the chip is live the instant this closes.
      //
      // The host's pre-flight (re-densify + persist the canonical transcript
      // BEFORE the server reads it from S3 — we intentionally don't ship
      // `elements`) runs INSIDE the run, so there is never a moment with neither
      // popover nor chip; its own failure is told apart below.
      let prepareFailed = false;
      const pending = translationStore.startRun(
        targetCode,
        {
          handler,
          // PLAT-10875: the active workspace's showcase, so the backend honors
          // this workspace's TM even if the story is in several.
          showcaseId: videoCreator.organization?.id,
          ...(willTranslateCompositions ? { compositionTargets } : {}),
          prepare: onBeforeGenerate
            ? async () => {
                try {
                  await onBeforeGenerate();
                } catch (error) {
                  prepareFailed = true;
                  throw error;
                }
              }
            : undefined,
        },
        {
          languageCaption,
          etaMs: estimateTranslateMs({
            transcriptElementCount:
              transcription.transcriptionElements?.length ?? null,
            compositionTargetCount: compositionTargets.length,
          }),
        },
      );
      // Cancelled from the chip (or from this popover, reopened): nothing to
      // report — the run rejects with an AbortError and the toasts stay quiet.
      let aborted = false;
      onClose();
      try {
        await withTranslationTracking(
          'generate',
          {
            storyId,
            sourceLang: sourceCode,
            targetLang: targetCode,
            handlerId: handler?.id,
          },
          async () => {
            const { compositions, translationMemoryReport } = await pending;
            compositionsOutcome = compositions;
            tmReport = translationMemoryReport;
          },
          () => {
            const translation = translationStore.translations.get(targetCode);
            return {
              handlerId: translation?.handler?.id,
              phraseCount: translation?.phrases?.length,
              // Distinct translation-memory entries the backend confirmed in
              // this run (PLAT-10853). `tmEntryIds` is honored-only (E7), so
              // this is what actually landed, not what matched.
              tmEntryCount: countDistinctTmEntries(translation?.phrases),
              showcaseId: videoCreator.organization?.id,
              // The QA verdicts (retried / missed) — not derivable from the
              // phrases, so they ride the response (PLAT-10924).
              tmReport,
            };
          },
          () => aborted,
        );
        if (willTranslateCompositions) {
          if (compositionsOutcome?.status === 'ok') {
            trackCompositionTranslationCompleted({
              story_id: storyId,
              language: targetCode,
              duration_ms: Math.round(performance.now() - tComp0),
              target_count: compositionTargets.length,
              unique_phrases: extract.uniqueSourceTextCount,
              preset_skipped: extract.presetSkipped,
              phrases_added: compositionsOutcome.phrasesAdded,
              phrases_skipped_pinned: compositionsOutcome.phrasesSkipped.pinned,
              phrases_orphaned: compositionsOutcome.phrasesOrphaned,
              from_bulk: true,
            });
          } else if (compositionsOutcome?.status === 'error') {
            trackCompositionTranslationFailed({
              story_id: storyId,
              language: targetCode,
              error_message: compositionsOutcome.message,
              error_code: compositionsOutcome.code,
              from_bulk: true,
            });
            // Transcript succeeded but compositions failed. The transcript
            // half is durable and the host still lands it (`onSuccess`); the
            // partial outcome is a warning toast now that the popover has
            // closed (PLAT-11690, as PLAT-11688 did for a partial dub). The
            // in-popover per-section Retry that used to live here is gone
            // with the popover; a re-run is a Regenerate.
            videoCreator.showToast({
              state: 'warning',
              message: `Transcript translated to ${languageCaption}, but composition translation failed.`,
            });
            onSuccess?.(targetCode);
            return;
          }
        }
        trackTranslationToggled({
          story_id: storyId,
          language: targetCode,
          action: 'on',
          view: 'transcript',
        });
        // The toast is the confirmation — the popover is long gone.
        videoCreator.showToast({
          state: 'success',
          message: `Translation to ${languageCaption} is ready.`,
          action: successAction?.(targetCode),
          secondaryAction: successSecondaryAction?.(targetCode),
        });
        onSuccess?.(targetCode);
      } catch (error) {
        if (isAbortError(error)) {
          aborted = true;
          return;
        }
        const message =
          error instanceof Error
            ? error.message
            : 'Translation failed. Try again.';
        // Above the prepareFailed return: the pre-flight runs inside the run,
        // so Started has already fired and the funnel has to close either way.
        if (willTranslateCompositions) {
          trackCompositionTranslationFailed({
            story_id: storyId,
            language: targetCode,
            error_message: message,
            from_bulk: true,
          });
        }
        if (prepareFailed) {
          videoCreator.showToast({
            state: 'error',
            message: `Could not save the latest transcript before translating to ${languageCaption}. Please try again.`,
          });
          return;
        }
        videoCreator.showToast({
          state: 'error',
          message: `Translation to ${languageCaption} failed. ${message}`,
        });
      }
    };

    const handleChooseFileClick = () => {
      fileInputRef.current?.click();
    };

    const runSrtUpload = async (file: File) => {
      if (targetCode === null) return;
      const controller = new AbortController();
      abortRef.current = controller;
      const storyId = rootStore.storyId ?? undefined;
      const t0 = performance.now();
      setPhase({ kind: 'uploading', filename: file.name });
      trackTranslationSrtUploadStarted({
        story_id: storyId,
        language: targetCode,
        filename: file.name,
        size_bytes: file.size,
        replaced_existing: isReplace,
      });
      try {
        const { coveragePercent, uncoveredCount } =
          await translationStore.uploadSrtTranslation(targetCode, file, {
            signal: controller.signal,
          });
        if (controller.signal.aborted) return;
        const phraseCount =
          translationStore.translations.get(targetCode)?.phrases?.length ?? 0;
        trackTranslationSrtUploadCompleted({
          story_id: storyId,
          language: targetCode,
          filename: file.name,
          duration_ms: Math.round(performance.now() - t0),
          phrase_count: phraseCount,
          coverage_percent: coveragePercent,
          uncovered_count: uncoveredCount,
          replaced_existing: isReplace,
        });
        trackTranslationToggled({
          story_id: storyId,
          language: targetCode,
          action: 'on',
          view: 'transcript',
        });
        // Show the coverage panel only when there's something the user
        // can actually act on. A `coveragePercent < 1` with no uncovered
        // words (rounding asymmetry between text-only coverage and the
        // uncovered-indices list) would otherwise produce the misleading
        // "0 words fell outside any SRT cue" copy.
        if (coveragePercent < 1 && uncoveredCount > 0) {
          setPhase({
            kind: 'uploaded',
            coveragePercent,
            uncoveredCount,
          });
        } else {
          onSuccess?.(targetCode);
          onClose();
        }
      } catch (error) {
        if (controller.signal.aborted || isAbortError(error)) return;
        const message =
          error instanceof Error
            ? error.message
            : 'SRT upload failed. Try again.';
        // Read structured fields off the typed error directly. The repo
        // throws `TranslationApiError` for any non-2xx; falling back to
        // undefined is safe because `error_code` is optional in the
        // analytics schema.
        const errorCode =
          error instanceof TranslationApiError ? error.code : undefined;
        trackTranslationSrtUploadFailed({
          story_id: storyId,
          language: targetCode,
          filename: file.name,
          error_message: message,
          error_code: errorCode,
          replaced_existing: isReplace,
        });
        setPhase({ kind: 'error', message });
      } finally {
        abortRef.current = null;
      }
    };

    const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // Allow re-selecting the same file later by resetting the input.
      event.target.value = '';
      if (!file) return;
      if (file.size > MAX_SRT_BYTES) {
        const mib = (file.size / (1024 * 1024)).toFixed(1);
        const limitMib = MAX_SRT_BYTES / (1024 * 1024);
        setSelectedFile(null);
        setPhase({
          kind: 'error',
          message: `SRT file is too large (${mib} MiB). Maximum size is ${limitMib} MiB. Please trim the file or upload a smaller export.`,
        });
        return;
      }
      setSelectedFile(file);
    };

    const handleCancel = () => {
      if (phase.kind === 'uploading' && abortRef.current) {
        abortRef.current.abort();
      }
      onClose();
    };

    // PLAT-11690 — reopened on the language being translated: Cancel aborts
    // the run the store owns (the chip's ✕ does the same) and closes.
    const handleCancelRun = () => {
      translationStore.cancelRun();
      onClose();
    };

    const handleRetry = () => {
      setPhase({ kind: 'idle' });
      // Retry follows whichever source is currently selected; the staged
      // file is preserved across an error, so the customer doesn't have
      // to re-pick on a transient backend failure.
      void handlePrimary();
    };

    /**
     * The single primary action. Dispatches by source: OpenAI runs the
     * LLM translator; SRT routes the staged file through the upload
     * endpoint. Both share the same "Generate" button label so the modal
     * has one obvious primary affordance regardless of source.
     */
    const handlePrimary = async () => {
      if (!canGenerate || targetCode === null) return;
      if (sourceId === 'srt-upload') {
        if (selectedFile === null) return;
        await runSrtUpload(selectedFile);
        return;
      }
      await handleGenerate();
    };

    const handleUploadedDone = () => {
      if (targetCode !== null) onSuccess?.(targetCode);
      onClose();
    };

    const isUploading = phase.kind === 'uploading';
    const isBusy = isUploading;
    const isRunning = runningThisTarget !== null;

    // Replace mode names what the selected source actually does: the LLM path
    // re-runs the translation ("Regenerate"), while an SRT upload swaps in a
    // file the customer authored elsewhere — nothing is regenerated, so that
    // path stays "Replace". Used for both the title and the primary action so
    // the two never disagree.
    const replaceVerb = sourceId === 'srt-upload' ? 'Replace' : 'Regenerate';

    return (
      <Modal
        isOpen
        // Escape, the ✕ and the backdrop just close: a running translation is
        // the page's now and a dismissed popover never orphans it.
        closeModal={isRunning ? onClose : handleCancel}
        disableEscapeClose={isBusy}
      >
        <DialogCard $size="md" ref={wrapperRef}>
          <DialogHeader>
            <DialogTitle>
              {isReplace
                ? `${replaceVerb} ${targetCaption ?? 'Translation'} Translation`
                : // PLAT-11877 — the dialog is named after the control that
                  // opens it (the toolbar and Languages-tab "Add Translation"
                  // and the coach mark under it all say this), so what you
                  // pressed and what opened agree.
                  'Add Translation'}
            </DialogTitle>
          </DialogHeader>

          <Body>
            {isRunning ? (
              // PLAT-11690 — the run is on the page (the chip beside the
              // language toggle); reopened here it reads the same clock.
              <GeneratingPanel role="status" aria-live="polite">
                <BigSpinner aria-hidden="true" />
                <GeneratingTitle>
                  Translating to {targetCaption ?? targetCode}…{' '}
                  {Math.round(runProgress)}%
                </GeneratingTitle>
                <GeneratingHint>
                  Running in the background. You can close this and keep working
                  — we&rsquo;ll let you know when it&rsquo;s ready.
                </GeneratingHint>
              </GeneratingPanel>
            ) : (
              <>
                <Section>
                  <SectionSublabel>Source Language</SectionSublabel>
                  <FixedValue aria-label="Source Language">
                    {sourceCaption}
                  </FixedValue>
                  <FieldHint>Set by the story&rsquo;s transcript.</FieldHint>
                </Section>

                <Section>
                  <SectionSublabel>Target Language</SectionSublabel>
                  {isReplace ? (
                    <>
                      <FixedValue aria-label="Target Language">
                        {/* Fall back to the BCP47 code if the showcase doesn't
                        list this language (defensive — a translation can exist
                        on disk for a language not in supportedLanguages). */}
                        {targetCaption ?? targetCode}
                      </FixedValue>
                      <FieldHint>
                        Locked to the translation you&rsquo;re replacing.
                      </FieldHint>
                    </>
                  ) : targetOptions.length === 0 ? (
                    <EmptyState>
                      No languages available to translate to. Contact your admin
                      to enable additional languages for this showcase.
                    </EmptyState>
                  ) : (
                    <SearchableLanguageSelect
                      options={targetOptions}
                      selectedValue={targetCode}
                      onSelect={setTargetCode}
                      placeholder="Select a language…"
                    />
                  )}
                </Section>

                <Section>
                  <SectionSublabel>Translation Source</SectionSublabel>
                  <SourceSelect
                    ariaLabel="Translation Source"
                    options={TRANSLATION_SOURCES.map(s => ({
                      value: s.id,
                      label: s.label,
                    }))}
                    selectedValue={sourceId}
                    disabled={isBusy}
                    onSelect={value => {
                      setSourceId(value as TranslationSourceId);
                      // Clear any staged file when switching off SRT mode so
                      // we don't carry forward an irrelevant filename.
                      if (value !== 'srt-upload') setSelectedFile(null);
                    }}
                  />
                  {previousSource && !showMethodChangeNotice && (
                    <SourceHelperText>
                      Currently using {previousSource.label}.
                    </SourceHelperText>
                  )}
                  {showMethodChangeNotice && previousSource && (
                    <MethodChangeNotice role="status">
                      Switching from <strong>{previousSource.label}</strong>.
                      The previous version remains in your history.
                    </MethodChangeNotice>
                  )}
                </Section>

                {sourceId === 'srt-upload' && (
                  <Section>
                    <SectionSublabel>SRT File</SectionSublabel>
                    <FilePickerRow>
                      <FilePickerFilename
                        aria-label="Selected SRT filename"
                        $hasFile={selectedFile !== null}
                      >
                        {selectedFile ? selectedFile.name : 'No file selected'}
                      </FilePickerFilename>
                      <SecondaryButton
                        type="button"
                        onClick={handleChooseFileClick}
                        disabled={isBusy}
                      >
                        {selectedFile ? 'Change…' : 'Choose File…'}
                      </SecondaryButton>
                    </FilePickerRow>
                  </Section>
                )}
              </>
            )}

            {phase.kind === 'uploaded' && (
              <CoveragePanel role="status">
                <CoverageHeading>SRT Processing Successful</CoverageHeading>
                <CoverageBody>
                  Coverage: {formatCoveragePercent(phase.coveragePercent)}%.{' '}
                  {phase.uncoveredCount} word
                  {phase.uncoveredCount === 1 ? '' : 's'} fell outside any SRT
                  cue and remain in the original language. You can install this
                  translation as-is or upload a corrected SRT.
                </CoverageBody>
              </CoveragePanel>
            )}

            {phase.kind === 'error' && (
              <ErrorRow role="alert">{phase.message}</ErrorRow>
            )}

            {!isRunning && runningHere !== null && phase.kind === 'idle' && (
              // PLAT-11690 — one translation at a time. Said where Generate
              // would otherwise seem broken.
              <FieldHint role="status">
                A translation to {runningHere.languageCaption} is already
                running. Generate is available when it finishes.
              </FieldHint>
            )}
          </Body>

          {/*
            Hidden file input — invoked by `Upload SRT…` to open the native
            picker. `accept=".srt"` filters the chooser; we don't rely on it
            for security (the server validates the body) but it gives the
            user a sensible default.
          */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".srt"
            aria-label="Upload SRT file"
            style={{ display: 'none' }}
            onChange={handleFileSelected}
          />

          <DialogFooter>
            {isRunning ? (
              <>
                <SecondaryButton type="button" onClick={handleCancelRun}>
                  Cancel Translation
                </SecondaryButton>
                <PrimaryButton type="button" onClick={onClose}>
                  Close
                </PrimaryButton>
              </>
            ) : isUploading ? (
              <>
                <GeneratingRow>
                  <Spinner />
                  <GeneratingLabel>
                    {`Uploading ${
                      phase.kind === 'uploading' ? phase.filename : ''
                    }…`}
                  </GeneratingLabel>
                </GeneratingRow>
                <SecondaryButton type="button" onClick={handleCancel}>
                  Cancel
                </SecondaryButton>
              </>
            ) : phase.kind === 'uploaded' ? (
              <>
                <SecondaryButton
                  type="button"
                  onClick={() => {
                    // Take the user back to the picker so they can choose
                    // a corrected SRT and click Generate again.
                    setSelectedFile(null);
                    setPhase({ kind: 'idle' });
                  }}
                >
                  Re-upload
                </SecondaryButton>
                <PrimaryButton type="button" onClick={handleUploadedDone}>
                  Done
                </PrimaryButton>
              </>
            ) : (
              <>
                <SecondaryButton type="button" onClick={handleCancel}>
                  Cancel
                </SecondaryButton>
                {phase.kind === 'error' ? (
                  <PrimaryButton type="button" onClick={handleRetry}>
                    Retry
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    type="button"
                    onClick={handlePrimary}
                    disabled={!canGenerate}
                  >
                    {isReplace ? replaceVerb : 'Generate'}
                  </PrimaryButton>
                )}
              </>
            )}
          </DialogFooter>
        </DialogCard>
      </Modal>
    );
  });

/**
 * Format a 0..1 coverage value as a whole-number percentage string for
 * display. Floors rather than rounds so the displayed number can never
 * overstate reality — a value of 0.997 with 3 uncovered words reads
 * "99% coverage / 3 words fell outside", not the self-contradictory
 * "100% coverage / 3 words fell outside". The clamp at 99 enforces that
 * invariant even if the panel ever displays at exactly 1.0.
 */
function formatCoveragePercent(coveragePercent: number): number {
  return Math.min(99, Math.floor(coveragePercent * 100));
}

interface SearchableLanguageSelectProps {
  options: { value: string; caption: string }[];
  selectedValue: string | null;
  onSelect: (value: string | null) => void;
  placeholder: string;
}

/**
 * Inline combobox: typeable filter over the target-language list. The input
 * displays the selected caption when closed; opening it reveals all options
 * and keystrokes narrow the list. Keyboard: ↑/↓ to navigate, Enter to pick,
 * Escape to close. Outside clicks close the dropdown.
 */
const SearchableLanguageSelect: React.FC<SearchableLanguageSelectProps> = ({
  options,
  selectedValue,
  onSelect,
  placeholder,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // The menu portals to <body> so it overlays the dialog instead of being
  // sized/clipped by it; the hook owns dismissal (outside click, scroll,
  // resize, Escape) — a wrapper-scoped listener can't see the portaled menu.
  const close = () => {
    setIsOpen(false);
    setQuery('');
  };
  const { menuRef, style: menuStyle } = useAnchoredDropdown(
    isOpen,
    rootRef,
    close,
    240,
  );

  const selectedCaption =
    options.find(o => o.value === selectedValue)?.caption ?? '';

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      o =>
        o.caption.toLowerCase().includes(q) ||
        o.value.toLowerCase().includes(q),
    );
  }, [options, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, isOpen]);

  const pick = (value: string) => {
    onSelect(value);
    close();
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      setActiveIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[activeIndex];
      if (opt) pick(opt.value);
    } else if (e.key === 'Escape') {
      close();
      inputRef.current?.blur();
    }
  };

  return (
    <Combobox ref={rootRef} $isOpen={isOpen}>
      <ComboboxInputRow
        onClick={() => {
          setIsOpen(true);
          inputRef.current?.focus();
        }}
      >
        <ComboboxInput
          ref={inputRef}
          type="text"
          value={isOpen ? query : selectedCaption}
          placeholder={selectedCaption ? '' : placeholder}
          onChange={e => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          aria-label="Target Language"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          role="combobox"
        />
        <ComboboxCaret aria-hidden="true">
          <ChevronDownIcon />
        </ComboboxCaret>
      </ComboboxInputRow>
      {isOpen &&
        menuStyle &&
        createPortal(
          <ComboboxDropdown ref={menuRef} style={menuStyle} role="listbox">
            {filtered.length === 0 ? (
              <ComboboxEmptyRow>
                No languages match &ldquo;{query}&rdquo;
              </ComboboxEmptyRow>
            ) : (
              filtered.map((opt, index) => (
                <ComboboxOption
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={opt.value === selectedValue}
                  data-active={index === activeIndex || undefined}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => pick(opt.value)}
                >
                  <OptionCaption>{opt.caption}</OptionCaption>
                  <OptionCode>{opt.value}</OptionCode>
                </ComboboxOption>
              ))
            )}
          </ComboboxDropdown>,
          document.body,
        )}
    </Combobox>
  );
};

interface SourceSelectOption {
  value: string;
  label: string;
}

interface SourceSelectProps {
  options: SourceSelectOption[];
  selectedValue: string;
  onSelect: (value: string) => void;
  disabled?: boolean;
  ariaLabel: string;
}

/**
 * Custom dropdown for the Translation source picker. Visually matched to the
 * target-language combobox above it (same trigger, caret, and overlay
 * styling) so the modal reads as a coherent set of Content Studio controls
 * rather than a native OS `<select>`. No type-to-filter input — the option
 * set is fixed and short, so the trigger just shows the current label.
 * Keyboard: ↑/↓ to move, Enter/Space to open or pick, Escape to close.
 */
const SourceSelect: React.FC<SourceSelectProps> = ({
  options,
  selectedValue,
  onSelect,
  disabled = false,
  ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  // Portaled + hook-owned dismissal, same as the target-language combobox.
  const { menuRef, style: menuStyle } = useAnchoredDropdown(
    isOpen,
    rootRef,
    () => setIsOpen(false),
    240,
  );

  const selectedLabel =
    options.find(o => o.value === selectedValue)?.label ?? '';

  useEffect(() => {
    if (!isOpen) return;
    setActiveIndex(
      Math.max(
        0,
        options.findIndex(o => o.value === selectedValue),
      ),
    );
  }, [isOpen, options, selectedValue]);

  const pick = (value: string) => {
    onSelect(value);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        setActiveIndex(i => Math.min(i + 1, options.length - 1));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        const opt = options[activeIndex];
        if (opt) pick(opt.value);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <Combobox ref={rootRef} $isOpen={isOpen} $disabled={disabled}>
      <SourceSelectTrigger
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        $disabled={disabled}
        onClick={() => {
          if (!disabled) setIsOpen(o => !o);
        }}
        onKeyDown={handleKeyDown}
      >
        <SourceSelectValue>{selectedLabel}</SourceSelectValue>
        <ComboboxCaret aria-hidden="true">
          <ChevronDownIcon />
        </ComboboxCaret>
      </SourceSelectTrigger>
      {isOpen &&
        menuStyle &&
        createPortal(
          <ComboboxDropdown ref={menuRef} style={menuStyle} role="listbox">
            {options.map((opt, index) => (
              <ComboboxOption
                key={opt.value}
                type="button"
                role="option"
                aria-selected={opt.value === selectedValue}
                data-active={index === activeIndex || undefined}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={e => e.preventDefault()}
                onClick={() => pick(opt.value)}
              >
                <OptionCaption>{opt.label}</OptionCaption>
              </ComboboxOption>
            ))}
          </ComboboxDropdown>,
          document.body,
        )}
    </Combobox>
  );
};

/* Bordered-field primitive — resting / hover / focus per
   docs/design-system.md "Form Fields". Focus (and the open menu) use the
   neutral gray focus tokens; green is reserved for action buttons. */
const Combobox = styled.div<{ $isOpen: boolean; $disabled?: boolean }>`
  border: 1px solid
    ${p => (p.$isOpen ? 'var(--focus-border)' : 'var(--border)')};
  box-shadow: ${p => (p.$isOpen ? '0 0 0 1px var(--focus-ring)' : 'none')};
  border-radius: var(--radius-input);
  background-color: var(--input-background);
  transition: border-color var(--transition-fast);
  /* Dim the whole control — border included — so a disabled field doesn't
     read as half-active with a full-strength border. */
  opacity: ${p => (p.$disabled ? 0.5 : 1)};

  /* A disabled field escalates on neither hover nor focus: an affordance it
     can't honour is worse than none. */
  ${p =>
    !p.$disabled &&
    `
  &:hover {
    border-color: ${p.$isOpen ? 'var(--focus-border)' : 'var(--text-muted)'};
  }

  &:focus-within {
    border-color: var(--focus-border);
    box-shadow: 0 0 0 1px var(--focus-ring);
  }
  `}
`;

const ComboboxInputRow = styled.div`
  display: flex;
  align-items: center;
  padding: var(--spacing-sm) var(--spacing-md);
  gap: var(--spacing-sm);
  cursor: text;
`;

const ComboboxInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  line-height: var(--leading-text-sm);

  &::placeholder {
    color: var(--text-muted);
  }
`;

const ComboboxCaret = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--accent);
  pointer-events: none;
`;

// Positioning (position/top/left/width/max-height/overflow/z-index) comes from
// useAnchoredDropdown as an inline style — the menu portals to <body> and
// anchors below its trigger. Only visuals live here.
const ComboboxDropdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  background-color: var(--background);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: var(--elevation-toast);
  padding: 4px;
`;

const ComboboxOption = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 32px;
  padding: 0 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--text-secondary);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  text-align: left;
  cursor: pointer;
  transition:
    background-color 120ms ease,
    color 120ms ease;

  &[data-active='true'] {
    background-color: var(--surface-elevated);
    color: var(--foreground);
  }

  &[aria-selected='true'] {
    color: var(--accent);
  }
`;

const OptionCaption = styled.span`
  flex: 1;
  font-weight: 500;
`;

const OptionCode = styled.span`
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

const ComboboxEmptyRow = styled.div`
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--text-sm);
  color: var(--text-muted);
  font-style: italic;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
`;

/* Field label. Deliberately a step BELOW the value it names (`--text-xs` vs
   the field's `--text-sm`) — at the same size and a heavier weight it competes
   with the value and reads as a heading rather than a label. */
const SectionSublabel = styled.div`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: var(--leading-text-xs);
  color: var(--text-secondary);
`;

/* A value the user can never change (the transcript's own language; the
   target in replace mode). Per docs/design-system.md's fixed/read-only rule
   it is shown BARE — no border, no field fill — so it doesn't advertise
   itself as a control the way a bordered box does. Its `FieldHint` says why
   it's fixed. Not dimmed: this is information to read, not a temporarily
   unavailable control. */
const FixedValue = styled.div`
  display: flex;
  align-items: center;
  min-height: var(--btn-h-md);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  line-height: var(--leading-text-sm);
  color: var(--foreground);
  cursor: default;
`;

/* One-line explanation under a fixed value — one step quieter than the
   field label so the two never read as peers. */
const FieldHint = styled.div`
  font-size: var(--text-xs);
  line-height: 1.4;
  color: var(--text-muted);
`;

/* Trigger row for the custom Translation source dropdown. Mirrors
   ComboboxInputRow's padding/gap so it lines up pixel-for-pixel with the
   target-language picker; the border/background live on the parent Combobox
   so open/focus styling is shared. */
const SourceSelectTrigger = styled.div<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  padding: var(--spacing-sm) var(--spacing-md);
  gap: var(--spacing-sm);
  cursor: ${p => (p.$disabled ? 'not-allowed' : 'pointer')};
  outline: none;

  &:focus-visible {
    border-radius: var(--radius-input);
    box-shadow: 0 0 0 1px var(--focus-ring);
  }
`;

const SourceSelectValue = styled.span`
  flex: 1;
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  line-height: var(--leading-text-sm);
`;

const SourceHelperText = styled.div`
  font-size: var(--text-xs);
  line-height: 1.4;
  color: var(--text-muted);
  padding: 0 2px;
`;

const MethodChangeNotice = styled.div`
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--text-sm);
  line-height: 1.4;
  color: var(--foreground);
  background-color: color-mix(in srgb, var(--accent) 10%, transparent);
  border: 1px solid var(--accent);
  border-radius: var(--radius-input);

  strong {
    font-weight: var(--font-weight-semi-bold);
  }
`;

const FilePickerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FilePickerFilename = styled.div<{ $hasFile: boolean }>`
  flex: 1;
  padding: var(--spacing-sm) var(--spacing-md);
  background-color: var(--input-background);
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  line-height: var(--leading-text-sm);
  color: ${({ $hasFile }) =>
    $hasFile ? 'var(--foreground)' : 'var(--text-muted)'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmptyState = styled.div`
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--text-sm);
  line-height: 140%;
  color: var(--foreground);
  opacity: 0.7;
  font-style: italic;
`;

const ErrorRow = styled.div`
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--text-sm);
  color: var(--destructive);
  background-color: color-mix(in srgb, var(--destructive) 12%, transparent);
  border: 1px solid var(--destructive);
  border-radius: var(--radius-input);
`;

// The upload succeeded — partial coverage is informational, not a warning.
// (The customer chose what they uploaded; the platform didn't fail at
// anything.) So this panel reads as success, matching the way the rest of
// the app uses --primary/green for completed actions.
const CoveragePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--text-sm);
  color: var(--primary);
  background-color: color-mix(in srgb, var(--primary) 12%, transparent);
  border: 1px solid var(--primary);
  border-radius: var(--radius-input);
`;

const CoverageHeading = styled.div`
  font-weight: var(--font-weight-semi-bold);
`;

const CoverageBody = styled.div`
  font-size: var(--text-xs);
  line-height: 1.4;
  opacity: 0.9;
`;

// Footer busy indicator — sits to the LEFT of the right-aligned action
// buttons per docs/design-system.md "Footer busy / progress state".
const GeneratingRow = styled.div`
  margin-right: auto;
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--foreground);
`;

const GeneratingLabel = styled.span`
  font-size: var(--text-sm);
  opacity: 0.85;
`;

const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: translation-spin 0.8s linear infinite;

  @keyframes translation-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// PLAT-10635: prominent in-body generating state so it's unmistakable that
// the translation is actively processing (the old 14px footer spinner read
// as ambient decoration).
const GeneratingPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
  padding: 20px 8px 12px;
`;

const BigSpinner = styled.span`
  display: inline-block;
  width: 40px;
  height: 40px;
  border: 3px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: translation-spin 0.8s linear infinite;

  @keyframes translation-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const GeneratingTitle = styled.div`
  font-size: var(--text-md);
  font-weight: var(--font-weight-semi-bold);
  color: var(--foreground);
`;

const GeneratingHint = styled.div`
  font-size: var(--text-sm);
  line-height: 1.5;
  color: var(--text-muted);
  max-width: 400px;
`;
